import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "@/components/site/Logo";

const links = [
  { label: "About", href: "#about" },
  { label: "Books", href: "#books" },
  { label: "Coming Soon", href: "#coming-soon" },
  { label: "Reviews", href: "#reviews" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/80 backdrop-blur-2xl border-b border-black/5 shadow-[0_8px_40px_rgba(0,0,0,0.07)]"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 sm:px-10 h-20 sm:h-24 flex items-center justify-between gap-4">
        <a
          href="#top"
          data-testid="nav-logo"
          className="shrink-0 group inline-flex items-center rounded-xl px-1 py-1 transition-all duration-300 hover:drop-shadow-[0_4px_16px_rgba(212,175,55,0.55)]"
          aria-label="Kate Mercer Books — home"
        >
          <Logo variant="dark" className="h-11 sm:h-14 transition-transform duration-300 group-hover:scale-[1.03]" />
        </a>

        <div className="hidden min-[900px]:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-sm font-medium text-gray-700 hover:text-black relative after:absolute after:-bottom-1.5 after:left-0 after:h-[2px] after:w-0 after:bg-gold after:transition-all after:duration-300 hover:after:w-full whitespace-nowrap"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#books"
            data-testid="nav-buy-cta"
            className="btn-gold rounded-full px-6 h-11 inline-flex items-center text-sm font-semibold"
          >
            Buy on Amazon
          </a>
        </div>

        <button
          className="min-[900px]:hidden p-2 -mr-1 flex items-center text-gray-900"
          onClick={() => setOpen((o) => !o)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          <AnimatePresence mode="wait" initial={false}>
            {open ? (
              <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
                <X size={28} />
              </motion.span>
            ) : (
              <motion.span key="m" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.2 }}>
                <Menu size={28} />
              </motion.span>
            )}
          </AnimatePresence>
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.28, ease: [0.2, 0.8, 0.2, 1] }}
            className="min-[900px]:hidden bg-white/90 backdrop-blur-2xl border-t border-black/5 px-8 py-8"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-6">
              {links.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 + i * 0.05 }}
                  className="text-lg font-medium text-gray-800"
                >
                  {l.label}
                </motion.a>
              ))}
              <a href="#books" onClick={() => setOpen(false)} className="btn-gold rounded-full py-4 text-sm font-semibold text-center mt-2">
                Buy on Amazon
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
